import { Checkbox } from '@components/Inputs/Checkbox/Checkbox';
import { css } from '@emotion/react';
import type { CellType, RowProps } from './TablePartsTypes';
import { HeadRow } from './HeadRow';
import { HeadCell } from './HeadCell';
import styled from '@emotion/styled';
import { Cell, Row } from './TableParts';
import * as tokens from '@tokens';
import type { HeadRowProps } from './HeadRow';
import type { HeadCellType } from './HeadCell';

export interface SelectableProps {
  /**
   * Selection change handler. Called when a row checkbox is changed.
   */
  onSelectionChange: () => void;
  /**
   * Specifies if the row is selected.
   */
  isSelected: boolean;
  /**
   * Accessible input description visible only to screen readers.
   */
  description?: string;
}

export interface SelectableRowProps extends RowProps, SelectableProps {}

export interface SelectableHeadRowProps extends HeadRowProps, SelectableProps {
  /**
   * Boolean that specifies if the checkbox is indeterminate. If set to true, we'll set the indeterminate attribute to the input
   * and display the checkbox with a minus icon to indicate the indeterminate state. Set this to true if some (not all) rows are selected.
   */
  isIndeterminate?: boolean;
}

const StyledRow = styled(Row)<Pick<SelectableRowProps, 'isSelected'>>`
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${tokens.color.bg_active};

      & td {
        border-bottom: 1px solid ${tokens.color.ui_border_active};
      }
    `}
`;

const StyledHeadCell = styled(HeadCell)`
  & > span {
    overflow: visible;
  }
`;

const ScreenReaderOnly = styled('span')`
  // Visually hidden style
  // https://github.com/twbs/bootstrap/blob/main/scss/mixins/_visually-hidden.scss
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;

  // Fix for positioned table caption that could become anonymous cells
  &:not(caption) {
    position: absolute !important;
  }
`;

export const SelectableRow = ({
  id,
  children,
  onSelectionChange,
  isSelected,
  description,
  ...props
}: SelectableRowProps): JSX.Element => {
  return (
    <StyledRow isSelected={isSelected} {...props}>
      <Cell>
        <Checkbox
          labelContent={<ScreenReaderOnly>{description}</ScreenReaderOnly>}
          id={`checkbox_table_row-${id}`}
          name={`checkbox_table_row-${id}`}
          onChange={onSelectionChange}
          isChecked={isSelected}
          value={`${isSelected}`}
        />
      </Cell>
      {children as CellType}
    </StyledRow>
  );
};

export const SelectableHeadRow = ({
  id,
  children,
  onSelectionChange,
  isIndeterminate = false,
  isSelected,
  ...props
}: SelectableHeadRowProps): JSX.Element => {
  return (
    <HeadRow {...props}>
      <StyledHeadCell>
        <Checkbox
          id={`checkbox_table_row-${id}`}
          name={`checkbox_table_row-${id}`}
          onChange={onSelectionChange}
          isChecked={isSelected}
          isIndeterminate={isIndeterminate}
          value={`${isSelected}`}
        />
      </StyledHeadCell>
      {children as HeadCellType}
    </HeadRow>
  );
};
